import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Clock, User, Phone, Mail, MessageSquare, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/t64300ho_SH%20Logo.png";

const AppointmentSection = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_date: '',
    preferred_time: '',
    department: '',
    message: '',
  });

  const departments = [
    { en: 'Gynaecology & Maternity', ta: 'மகப்பேறு & பேறுகால பராமரிப்பு' },
    { en: 'Cardiology', ta: 'இதயவியல்' },
    { en: 'Urology', ta: 'சிறுநீரகவியல்' },
    { en: 'General Medicine', ta: 'பொது மருத்துவம்' },
    { en: 'Skin & Hair Care', ta: 'தோல் & முடி பராமரிப்பு' },
    { en: 'Orthopaedics', ta: 'எலும்பியல்' },
    { en: 'Gastroenterology', ta: 'இரைப்பை குடல் இயல்' },
    { en: 'Dialysis', ta: 'டயாலிசிஸ்' },
  ];

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM',
    '04:30 PM', '05:00 PM', '05:30 PM', '06:00 PM', '06:30 PM', '07:00 PM',
    '07:30 PM', '08:00 PM', '08:30 PM',
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.department) {
      toast.error(language === 'ta' ? 'அனைத்து தேவையான புலங்களையும் நிரப்பவும்' : 'Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || `${formData.phone}@callback.sakthi`,
        preferred_date: formData.preferred_date || new Date().toISOString().split('T')[0],
        preferred_time: formData.preferred_time || 'Callback Requested',
        department: formData.department,
        message: formData.message || 'Quick Appointment - Callback Requested',
        language,
      });
      setSuccess(true);
      toast.success(language === 'ta' ? 'நன்றி! எங்கள் குழு விரைவில் உங்களை தொடர்பு கொள்ளும்.' : 'Thank you! Our team will call you back shortly.');
      setFormData({
        name: '',
        phone: '',
        email: '',
        preferred_date: '',
        preferred_time: '',
        department: '',
        message: '',
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      toast.error(t('appointment.error'));
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <section id="appointment" className="py-14 bg-white relative overflow-hidden" data-testid="appointment-section">
      <div className="max-w-3xl mx-auto px-4 relative z-10">
        {/* Family Consultation Image */}
        <div className="rounded-2xl overflow-hidden shadow-lg mb-0">
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_health-portal-129/artifacts/a2rrfpek_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2001_57_56%20PM.png"
              alt="Family consulting with doctor at Sakthi Hospital - Caring like family, Healing with trust"
              className="w-full h-[320px] md:h-[380px] object-cover"
              data-testid="appointment-family-image"
            />
          </div>
        </div>

        {/* Quick Appointment Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 -mt-6 relative z-10" data-testid="appointment-form">
          {success ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 bg-[#0066b3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-[#0066b3]" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2b3c] mb-2">
                {language === 'ta' ? 'நன்றி!' : 'Thank You!'}
              </h3>
              <p className="text-[#5a6b7c]">{t('appointment.success')}</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1a2b3c]">
                  {language === 'ta' ? 'விரைவு சந்திப்பு' : 'Quick Appointment'}
                </h2>
                <p className="text-[#5a6b7c] text-sm mt-1">
                  {language === 'ta' ? 'நாங்கள் விரைவில் உங்களை தொடர்பு கொள்வோம்' : "We'll call you back shortly"}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder={language === 'ta' ? 'உங்கள் பெயர்' : 'Your Name'}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="h-13 px-4 py-3.5 border border-gray-200 rounded-lg text-base bg-white focus:border-[#0066b3] focus:ring-1 focus:ring-[#0066b3]"
                  required
                  data-testid="appointment-name-input"
                />

                <Input
                  type="tel"
                  placeholder={language === 'ta' ? 'தொலைபேசி எண்' : 'Phone Number'}
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="h-13 px-4 py-3.5 border border-gray-200 rounded-lg text-base bg-white focus:border-[#0066b3] focus:ring-1 focus:ring-[#0066b3]"
                  required
                  data-testid="appointment-phone-input"
                />

                <Select
                  value={formData.department}
                  onValueChange={(value) => handleChange('department', value)}
                >
                  <SelectTrigger className="h-13 px-4 py-3.5 border border-gray-200 rounded-lg text-base bg-white focus:border-[#0066b3] focus:ring-1 focus:ring-[#0066b3]" data-testid="appointment-department-select">
                    <SelectValue placeholder={language === 'ta' ? 'துறையை தேர்ந்தெடுக்கவும்' : 'Select Department'} />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.en} value={dept.en}>
                        {language === 'ta' ? dept.ta : dept.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-[#0066b3] hover:bg-[#004a82] text-white text-base font-semibold rounded-lg transition-colors shadow-md shadow-[#0066b3]/20"
                  data-testid="appointment-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin mr-2" />
                      {language === 'ta' ? 'சமர்ப்பிக்கிறது...' : 'Booking...'}
                    </>
                  ) : (
                    <>
                      {language === 'ta' ? 'இப்போது முன்பதிவு செய்யுங்கள்' : 'Book Now'}
                      <ArrowRight size={18} className="ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-6 mt-5">
                <span className="flex items-center gap-1.5 text-sm text-[#3a4a5c]">
                  <CheckCircle size={16} className="text-emerald-500" />
                  {language === 'ta' ? 'இலவச ஆலோசனை' : 'Free Consultation'}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-[#3a4a5c]">
                  <CheckCircle size={16} className="text-emerald-500" />
                  {language === 'ta' ? 'விரைவான பதில்' : 'Quick Response'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default AppointmentSection;
